import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { useRole } from "@/lib/roleContext";
import { formatDate, eventPriceRange } from "@/lib/format";
import { CalendarDays, Pencil, MapPin, Plus, BarChart3, Trash2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function WorkspaceEvents() {
    const { activeArtistId } = useRole();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("upcoming");
    const [pending, setPending] = useState({ pending_events: [], declined_events: [] });
    const [deletingId, setDeletingId] = useState(null);

    const load = () => {
        if (!activeArtistId) return;
        setLoading(true);
        endpoints
            .workspaceEvents(activeArtistId)
            .then((d) => {
                setData(d);
                setLoading(false);
            })
            .catch(() => setLoading(false));
        endpoints.artistPendingEvents(activeArtistId).then(setPending).catch(() => {});
    };

    useEffect(load, [activeArtistId]);

    const accept = async (ev) => {
        try { await endpoints.artistAcceptPendingEvent(activeArtistId, ev.id); toast.success("Accepted — event added as draft"); load(); }
        catch { toast.error("Could not accept"); }
    };
    const decline = async (ev) => {
        if (!window.confirm(`Decline "${ev.name}" from ${ev.venue_name}?`)) return;
        try { await endpoints.artistDeclinePendingEvent(activeArtistId, ev.id); toast.success("Declined"); load(); }
        catch { toast.error("Could not decline"); }
    };

    const togglePublish = async (ev) => {
        try {
            await endpoints.updateEvent(ev.id, { published: !ev.published });
            toast.success(ev.published ? "Unpublished" : "Published");
            load();
        } catch {
            toast.error("Could not update");
        }
    };

    const deleteDraft = async (ev) => {
        if (ev.status !== "draft") return;
        if (!window.confirm(`Delete this draft event? This cannot be undone.\n\n"${ev.name}"`)) return;
        setDeletingId(ev.id);
        try {
            await endpoints.deleteEvent(ev.id);
            toast.success("Draft deleted");
            load();
        } catch { toast.error("Could not delete draft"); } finally { setDeletingId(null); }
    };

    return (
        <div className="pm-fade" data-testid="workspace-events-page">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6 flex items-center justify-between">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Events</div>
                    <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Manage events</h1>
                </div>
                <Link to="/workspace/events/new" className="pm-btn pm-btn-primary" data-testid="ws-events-create-btn">
                    <Plus className="w-4 h-4 mr-2" /> New event
                </Link>
            </header>

            <div className="px-4 md:px-8 py-6">
                {pending.pending_events.length > 0 && (
                    <div className="mb-4 border border-[#ff9500] p-4 bg-[#141414]" data-testid="workspace-pending-events-banner">
                        <div className="text-[10px] font-mono uppercase text-[#ff9500] mb-2">Venue requests · {pending.pending_events.length} awaiting your response</div>
                        <div className="space-y-2">
                            {pending.pending_events.map((e) => (
                                <div key={e.id} className="border border-[#2a2a2a] p-3 flex flex-wrap gap-2 items-center justify-between" data-testid={`ws-pending-${e.id}`}>
                                    <div className="min-w-0">
                                        <div className="font-display font-bold uppercase text-sm">{e.name}</div>
                                        <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">{formatDate(e.date)} · {e.venue_name}</div>
                                    </div>
                                    <div className="flex gap-1">
                                        <button onClick={() => accept(e)} className="pm-btn pm-btn-primary text-[10px]" data-testid={`ws-pending-accept-${e.id}`}>Accept</button>
                                        <button onClick={() => decline(e)} className="pm-btn text-[10px] text-[#ff3333] border-[#ff3333]" data-testid={`ws-pending-decline-${e.id}`}>Decline</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className="bg-transparent border border-[#2a2a2a] rounded-none p-0 h-auto">
                        <TabsTrigger value="upcoming" data-testid="tab-upcoming" className="rounded-none data-[state=active]:bg-[#ff4400] data-[state=active]:text-white font-mono uppercase tracking-wider text-xs px-4 py-2">Upcoming ({data?.grouped.upcoming.length || 0})</TabsTrigger>
                        <TabsTrigger value="draft" data-testid="tab-draft" className="rounded-none data-[state=active]:bg-[#ff4400] data-[state=active]:text-white font-mono uppercase tracking-wider text-xs px-4 py-2">Drafts ({data?.grouped.draft.length || 0})</TabsTrigger>
                        <TabsTrigger value="past" data-testid="tab-past" className="rounded-none data-[state=active]:bg-[#ff4400] data-[state=active]:text-white font-mono uppercase tracking-wider text-xs px-4 py-2">Past ({data?.grouped.past.length || 0})</TabsTrigger>
                    </TabsList>

                    {loading && <div className="mt-6 font-mono text-sm text-[#666]">Loading…</div>}

                    {["upcoming", "draft", "past"].map((k) => (
                        <TabsContent key={k} value={k} className="mt-6">
                            {data?.grouped[k].length === 0 ? (
                                <div className="border border-[#2a2a2a] p-10 text-center text-[#a3a3a3]" data-testid={`ws-events-empty-${k}`}>
                                    No {k} events.
                                </div>
                            ) : (
                                <div className="border border-[#2a2a2a]">
                                    {data?.grouped[k].map((e) => (
                                        <div key={e.id} className="flex flex-col md:flex-row gap-4 items-stretch border-b border-[#2a2a2a] last:border-b-0" data-testid={`ws-event-row-${e.id}`}>
                                            <div className="w-full md:w-40 aspect-video md:aspect-auto md:h-32 relative overflow-hidden shrink-0">
                                                <img src={e.image_url} alt="" className="absolute inset-0 w-full h-full object-cover grayscale" />
                                            </div>
                                            <div className="flex-1 p-4 flex flex-col md:flex-row md:items-center gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[10px] font-mono uppercase text-[#ff4400]">{e.event_type} · {formatDate(e.date)}</div>
                                                    <div className="font-display font-black uppercase text-lg leading-tight mt-1">{e.name}</div>
                                                    <div className="text-xs text-[#a3a3a3] mt-1 flex items-center gap-1">
                                                        <MapPin className="w-3 h-3" /> {e.venue_name} · {e.city}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-3 gap-3 md:gap-6 text-center">
                                                    <div>
                                                        <div className="text-[10px] font-mono uppercase text-[#666]">Reg</div>
                                                        <div className="font-display font-black">{e.registrations}/{e.capacity}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-[10px] font-mono uppercase text-[#666]">Price</div>
                                                        <div className="font-display font-black text-sm">{eventPriceRange(e)}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-[10px] font-mono uppercase text-[#666]">Status</div>
                                                        <div className={`font-mono uppercase text-xs ${e.published ? "text-[#00e676]" : "text-[#ffea00]"}`}>
                                                            {e.published ? "Live" : "Draft"}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2 flex-wrap min-w-0">
                                                    <Link to={`/workspace/events/${e.id}/edit`} className="pm-btn text-xs" data-testid={`ws-event-edit-${e.id}`}>
                                                        <Pencil className="w-3 h-3 mr-1" /> Edit
                                                    </Link>
                                                    {e.status === "past" && (
                                                        <Link to={`/workspace/events/${e.id}/performance`} className="pm-btn text-xs" data-testid={`ws-event-performance-${e.id}`}>
                                                            <BarChart3 className="w-3 h-3 mr-1" /> Performance
                                                        </Link>
                                                    )}
                                                    <button onClick={() => togglePublish(e)} className="pm-btn text-xs" data-testid={`ws-event-toggle-${e.id}`}>
                                                        {e.published ? "Unpublish" : "Publish"}
                                                    </button>
                                                    {e.status === "draft" && (
                                                        <button
                                                            onClick={() => deleteDraft(e)}
                                                            disabled={deletingId === e.id}
                                                            className="pm-btn text-xs text-[#ff3333] border-[#ff3333]"
                                                            data-testid={`ws-event-delete-draft-${e.id}`}
                                                        >
                                                            <Trash2 className="w-3 h-3 mr-1" /> Delete draft
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </TabsContent>
                    ))}
                </Tabs>
            </div>
        </div>
    );
}
